import { useEffect } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Box, Button, Icon, Input, Modal, TextArea } from 'components'
import { UploadHint } from 'components/UploadHint'

import { useApp } from 'store/app/hooks'
import { useGame } from 'store/game/hooks'

import { CreateGameInput } from './types'

export const CreateGameModal = () => {
  const { isCreateGame, newGame, error, createGame } = useGame()
  const { isCreateGameModalOpen, setIsCreateGameModalOpen } = useApp()

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<CreateGameInput>()

  const onSubmit = (data: CreateGameInput): void => {
    console.log('Form submitted with data:', data)
    console.log('Avatar:', data.avatar)
    console.log('Cover photo:', data.cover_photo)
    console.log('Name:', data.name)
    console.log('URL:', data.url)
    console.log('Description:', data.description)
    createGame(data)
  }

  useEffect(() => {
    if (error) {
      toast(error)
    }
  }, [error])

  useEffect(() => {
    if (!isCreateGame && newGame) {
      reset({
        avatar: null,
        cover_photo: null,
        name: '',
        url: '',
        description: '',
      })
      setIsCreateGameModalOpen(false)
    }
  }, [isCreateGame, newGame, reset, setIsCreateGameModalOpen])

  useEffect(() => {
    reset({
      avatar: null,
      cover_photo: null,
      name: '',
      url: '',
      description: '',
    })
  }, [isCreateGameModalOpen])

  return (
    <Modal
      title="Create Game"
      isOpened={isCreateGameModalOpen}
      onClose={() => setIsCreateGameModalOpen(false)}
      withCard={true}
    >
      <Box className="w-full" col>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <Controller
              control={control}
              name="cover_photo"
              render={({ field }) => (
                <Box
                  className="w-full aspect-[4/1] relative bg-gray-800 rounded-lg overflow-hidden"
                  justifyCenter
                  alignCenter
                >
                  {field.value && (
                    <img
                      src={URL.createObjectURL(field.value)}
                      className="absolute w-full h-full z-0 object-cover"
                      alt="Cover preview"
                    />
                  )}
                  <label htmlFor="cover-upload" className="z-10">
                    <Box
                      className="w-[44px] h-[44px] rounded-full bg-gradient-primary-dark cursor-pointer hover:opacity-80"
                      justifyCenter
                      alignCenter
                    >
                      <Icon name="camera" />
                    </Box>
                    <input
                      accept="image/*"
                      className="hidden"
                      id="cover-upload"
                      type="file"
                      onChange={(e) => {
                        const fileList = e.target.files
                        if (!fileList || fileList.length === 0) return
                        console.log('Cover photo selected:', fileList[0])
                        field.onChange(fileList[0])
                      }}
                    />
                  </label>
                </Box>
              )}
            />
            <Controller
              control={control}
              name="avatar"
              render={({ field }) => (
                <div className="relative w-32 h-32 -mt-16 ml-4 z-10 mb-2">
                  <div className="relative w-32 h-32 bg-gray-700 rounded-full border-4 border-[#0a0e10] overflow-hidden">
                    {field.value && (
                      <img
                        src={URL.createObjectURL(field.value)}
                        className="w-full h-full rounded-full object-cover"
                        alt="Avatar preview"
                      />
                    )}
                    <label htmlFor="avatar-upload">
                      <Box
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[44px] h-[44px] rounded-full bg-gradient-primary-dark cursor-pointer hover:opacity-80"
                        justifyCenter
                        alignCenter
                      >
                        <Icon name="camera" />
                      </Box>
                      <input
                        accept="image/*"
                        className="hidden"
                        id="avatar-upload"
                        type="file"
                        onChange={(e) => {
                          const fileList = e.target.files
                          if (!fileList || fileList.length === 0) return
                          console.log('Avatar selected:', fileList[0])
                          field.onChange(fileList[0])
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 pt-1">
            <UploadHint type="banner" />
            <UploadHint type="avatar" />
          </div>
          <br />
          <Controller
            control={control}
            name="name"
            rules={{
              required: 'This is required.',
            }}
            render={({ field }) => (
              <Input
                placeholder="Name"
                stretch
                border="bottom"
                icon="account"
                error={errors?.name?.message}
                onChange={(e) => {
                  field.onChange(`${e.target.value}`)
                }}
                value={field.value}
              />
            )}
          />
          <br />
          <Controller
            control={control}
            name="url"
            render={({ field }) => (
              <Input
                placeholder="Website URL (optional)"
                stretch
                border="bottom"
                icon="link"
                error={errors?.url?.message}
                onChange={(e) => {
                  field.onChange(`${e.target.value}`)
                }}
                value={field.value}
              />
            )}
          />
          <br />
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <TextArea
                rows={3}
                stretch
                autoFocus
                placeholder="Write game description here."
                error={errors?.description?.message}
                onChange={(e) => {
                  field.onChange(`${e.target.value}`)
                }}
                value={field.value}
              />
            )}
          />
          <Button
            className="mt-6 w-full"
            size="md"
            isFancy
            loading={isCreateGame}
            buttonType="submit"
          >
            Submit
          </Button>
        </form>
      </Box>
    </Modal>
  )
}
